import * as ActionTypes from './actionTypes';
import { mapToFullAppRequest, mapToLoanApplication } from './mapToApi';
import { createApiClient } from '../api.client';
import { applicationStatusFetchFailed, startLoading, finishLoading, displayErrors, updateCampaignData } from '../actions';
import { isEmpty, has, mergeAll, pickBy, pick } from 'ramda';
import { SubmissionError, change } from 'redux-form';
import { transformToISO } from "../utils/date.transfomers";
import defaultFetch from "isomorphic-fetch";
import appStatusFetchRetry from "../app.status.fetch.retry";
import { getDTMData } from "../selectors";
import RetryError from '../utils/retryError';
import retry from '../utils/retry';
import isPromise from 'is-promise';
export const FORM_ERROR_NOTIFICATION = { description: 'Please correct the errors on the fields marked in red' };

/* ACTION HELPERS */

export const mapErrors = (errors) => {
  let validationErrors = errors.reduce((accumulator, error) => {
    if (has('property', error)) {
      Object.assign(accumulator, { [error.property]: error.description });
    }
    return accumulator;
  }, {});
  let globalErrors = errors.filter(error => !has('property', error));

  if (!isEmpty(validationErrors)) {
    globalErrors.unshift(FORM_ERROR_NOTIFICATION);
  }

  return { validationErrors, globalErrors };
};

export const getBarcaoformData = () => {
  return window.barcaoparm !== null ? window.barcaoparm.barcaoform() : {};
};

const notBlank = (val, key) => val !== '';

const filteredHeaders = (dtmData) => pick(['cj_tagEvent', 'referrer_id', 'site_name', 'affiliate_name', 'advertisementId', 'shopperId', 'publisherId'], dtmData);

/* PURE ACTIONS */

export const applicationDataReset = () => ({
  type: ActionTypes.APPLICATION_DATA_RESET
});

export const landingFormSubmitted = (formData) => ({
  type: ActionTypes.LANDING_FORM_SUBMITTED,
  meta: { dtmTag: true },
  data: formData
});

export const prospectValidated = (formData) => ({
  type: ActionTypes.PROSPECT_VALIDATED,
  meta: { dtmTag: true },
  data: formData
});

export const checkYourRateFormSubmitted = (formData) => ({
  type: ActionTypes.CHECK_YOUR_RATE_FORM_SUBMITTED,
  data: formData
});

export const quoteReceived = (quote) => ({
  type: ActionTypes.QUOTE_RECEIVED,
  meta: {
    dtmTag: true,
    focus: 'quotesReceived'
  },
  quote: quote
});

export const selectOfferFormSubmitted = (formData) => ({
  type: ActionTypes.SELECT_OFFER_FORM_SUBMITTED,
  meta: {
    dtmTag: true
  },
  data: formData
});

export const customerInfoFromTokenReceived = (loanApplication) => ({
  type: ActionTypes.CUSTOMER_INFO_FROM_TOKEN_RECEIVED,
  loanApplication: loanApplication
});

export const loanApplicationReceived = (loanApplicationPrePopData) => ({
  type: ActionTypes.LOAN_APPLICATION_RECEIVED,
  meta: { dtmTag: true, updateDTMEventOnly: true },
  loanApplication: loanApplicationPrePopData
});

export const applicationDecision = ({ pageId, applicationStatus, referenceNumber, applicationId }) => {
  if (!referenceNumber) throw TypeError('Missing referenceNumber');
  if (!pageId) throw TypeError('Missing pageId');
  const meta = pageId === 'verid' ? undefined : { dtmTag: true, focus: pageId };
  return {
    type: ActionTypes.APPLICATION_DECISION,
    meta: meta,
    applicationDecision: { pageId, applicationStatus, referenceNumber, applicationId }
  };
};

export const submitInvitationCode = ({ invitationCode, lastName }) => {
  const apiClient = createApiClient();
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      let prospect = await apiClient.postForm('/clapply/cl/CLLanding.action', { invitationCode, lastName }, 'validateInvitationCode');
      let campaign = await apiClient.postForm('/clapply/cl/CLLanding.action', null, 'getPreApplyDetails');
      dispatch(prospectValidated({ prospect, campaign }));
      dispatch(finishLoading());
    } catch (errors) {
      let { validationErrors, globalErrors } = mapErrors(errors);
      dispatch(displayErrors(globalErrors));
      dispatch(finishLoading());
      throw new SubmissionError(validationErrors);
    }
  };
};

export const submitCheckYourRate = (data, throwOnNullCyrToggle) => {
  const apiClient = createApiClient();
  return async (dispatch, getState) => {
    let dtmData = getDTMData(getState());
    let headers = pickBy(notBlank, filteredHeaders(dtmData));

    try {
      dispatch(startLoading());

      if (throwOnNullCyrToggle === true) {
        let cyrRequest = mergeAll([data, { dateOfBirth: transformToISO(data.dateOfBirth) }, { barcaoform: getBarcaoformData() }]);
        let cyrResponse = await apiClient.postForm('/clapply/cl/CLCyr.action', cyrRequest, 'submitCheckYourRate', headers);

        let { applicationId, quoteId } = cyrResponse;

        if (!applicationId || !quoteId) {
          await apiClient.postForm('/clapply/cl/CLError.action', { cyrResponse: JSON.stringify(cyrResponse) }, 'logCYRFailure');

          window.location.assign('/clapply/cl/CLError.action');
        }

        dispatch(checkYourRateFormSubmitted(Object.assign(data, { applicationId, quoteId })));
      } else {
        let requestBody = mergeAll([data, { dateOfBirth: transformToISO(data.dateOfBirth) }, { barcaoform: getBarcaoformData() }]);
        let { applicationId, quoteId } = await apiClient.postForm('/clapply/cl/CLCyr.action', requestBody, 'submitCheckYourRate', headers);

        dispatch(checkYourRateFormSubmitted(Object.assign(data, { applicationId, quoteId })));
      }
    } catch (errors) {
      let { validationErrors, globalErrors } = mapErrors(errors);
      dispatch(displayErrors(globalErrors));
      dispatch(finishLoading());
      throw new SubmissionError(validationErrors);
    }
  };
};

export const getQuotes = (applicationId, quoteId) => {
  const apiClient = createApiClient({ fetchRetry: retry });
  return async (dispatch) => {
    try {
      let quotes = await apiClient.fetchWithRetries('/clapply/cl/CLCyr.action', 'fetchQuotes', null, { applicationId: applicationId, quoteId: quoteId });
      dispatch(quoteReceived(quotes));
      dispatch(finishLoading());

    } catch (e) {
      if (e instanceof RetryError) {
        await apiClient.postForm('/clapply/cl/CLError.action', { errorAction: 'fetchQuotes', applicationId: applicationId, quoteId: quoteId }, 'logRetryFailure');
      }

      window.location.assign('/clapply/cl/CLError.action');
    }
  };
};

export const updateFormValue = (form, field, value) => {
  return (dispatch) => dispatch(change(form, field, value));
};

export const submitFullApp = (data) => {
  const apiClient = createApiClient({ fetch: defaultFetch, fetchRetry: appStatusFetchRetry });
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      let requestBody = mergeAll([data, { barcaoform: getBarcaoformData() }]);
      await apiClient.postForm('/clapply/cl/CLApplication.action', { applicationId: data.applicationId, submitApplicationRequest: mapToFullAppRequest(requestBody) }, 'submitFullApp');
      let { pageId, applicationStatus, referenceNumber, applicationId } = await apiClient.fetchWithRetries('/clapply/cl/CLApplication.action', 'getApplicationStatus');
      dispatch(applicationDecision({ pageId: pageId, applicationStatus: applicationStatus, referenceNumber: referenceNumber, applicationId: applicationId }));
      dispatch(finishLoading());
    } catch (e) {
      if (e instanceof RetryError) {
        let { pageId, applicationStatus, referenceNumber, applicationId } = await e.response.json();
        await apiClient.postForm('/clapply/cl/CLError.action', { errorAction: 'getApplicationStatus', applicationId: applicationId, referenceNumber: referenceNumber }, 'logRetryFailure');
        dispatch(applicationStatusFetchFailed({ pageId, applicationStatus, referenceNumber }));
      } else {
        let { validationErrors, globalErrors } = mapErrors(e);
        dispatch(displayErrors(globalErrors));
        dispatch(finishLoading());
        throw new SubmissionError(validationErrors);
      }
    }
  };
};

export const fetchUpdatedCampaignData = () => {
  const apiClient = createApiClient();
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const campaignData = await apiClient.getWithRetries('/clapply/cl/campaign', null);
      dispatch(updateCampaignData(campaignData));
    } catch (errors) {
      dispatch(displayErrors(errors));
    } finally {
      dispatch(finishLoading());
    }
  };
};

export const fetchCustomerInfoFromToken = (token) => {
  const apiClient = createApiClient();
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const customerInfo = await apiClient.postForm('/clapply/cl/CLDeepLinkOffer.action', { token }, 'fetchCustomerData');
      dispatch(customerInfoFromTokenReceived(mapToLoanApplication(customerInfo)));
    } catch (errors) {
      dispatch(displayErrors(errors));
    } finally {
      dispatch(finishLoading());
    }
  };
};

export const fetchCustomerInfoFromTokenWithCallback = (token, fetchUpdatedCampaignDataCallback) => {
  const apiClient = createApiClient();
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const customerInfo = await apiClient.postForm('/clapply/cl/CLDeepLinkOffer.action', { token }, 'fetchCustomerData');
      fetchUpdatedCampaignDataCallback();
      dispatch(customerInfoFromTokenReceived(mapToLoanApplication(customerInfo)));
    } catch (errors) {
      dispatch(displayErrors(errors));
    } finally {
      dispatch(finishLoading());
    }
  };
};

export const fetchLoanApplicationFromToken = (token) => {
  const apiClient = createApiClient();
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const loanApplicationPrePopData = await apiClient.postForm('/clapply/cl/CLLogin.action', { token }, 'applicationLogin');
      dispatch(loanApplicationReceived(loanApplicationPrePopData));
    } catch (errors) {
      dispatch(displayErrors(errors));
    } finally {
      dispatch(finishLoading());
    }
  };
};

export const fetchLoanApplicationPrePopData = (token, apr, termInMonths, verificationCode) => {
  const apiClient = createApiClient();
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const loanApplicationPrePopData = await apiClient.postForm('/clapply/cl/CLAffiliateLogin.action', { token, apr, termInMonths, verificationCode }, 'affiliateApplicationLogin');
      dispatch(loanApplicationReceived(loanApplicationPrePopData));
    } catch (errors) {
      dispatch(displayErrors(errors));
    } finally {
      dispatch(finishLoading());
    }
  };
};

export const displayFormErrorOnSubmit = (submitResult) => {
  return (dispatch) => {
    if (!isPromise(submitResult)) {
      dispatch(displayErrors([FORM_ERROR_NOTIFICATION]));
    }
    return submitResult;
  };
};

export const markStepAsInterstitial = () => ({
  type: 'markStepAsInterstitial'
});

export const fireDtmController = () => ({
  type: 'fireDtmController',
  meta: { dtmTag: true }
});

export const toggleNoInvitationDialog = () => {
  return async (dispatch) => {
    dispatch({ type: 'toggleNoInvitationDialog' });
  };
};
